import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import * as React from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { HelmetProvider } from 'react-helmet-async'
import { ThemeProvider as MuiThemeProvider, CssBaseline, Box } from '@mui/material'

import { MainErrorFallback } from '@/components/errors/main'
import { Notifications } from '@/components/ui/notifications'
import { Spinner } from '@/components/ui/spinner'
import { queryConfig } from '@/lib/react-query'
import { createAppTheme, ThemeContext, ThemeMode } from '@/lib/mui-theme'
import { AuthLoader } from '@/lib/auth'
import 'mapbox-gl/dist/mapbox-gl.css'

type AppProviderProps = {
  children: React.ReactNode
}

// ローディングコンテナコンポーネント
const LoadingContainer = () => (
  <Box
    sx={{
      display: 'flex',
      height: '100vh',
      width: '100vw',
      alignItems: 'center',
      justifyContent: 'center',
    }}>
    <Spinner size='xl' />
  </Box>
)

export const AppProvider = ({ children }: AppProviderProps) => {
  const [queryClient] = React.useState(
    () =>
      new QueryClient({
        defaultOptions: queryConfig,
      }),
  )

  // MUIテーマの状態管理
  const [mode, setMode] = React.useState<ThemeMode>(() => {
    // クライアントサイドでのみ実行
    if (typeof window !== 'undefined') {
      const savedMode = localStorage.getItem('themeMode') as ThemeMode | null
      return savedMode === 'dark' ? 'dark' : 'light'
    }
    return 'light'
  })

  // テーマの切り替え関数
  const toggleColorMode = React.useCallback(() => {
    setMode((prevMode) => {
      const newMode = prevMode === 'light' ? 'dark' : 'light'
      // ローカルストレージに保存
      if (typeof window !== 'undefined') {
        localStorage.setItem('themeMode', newMode)
      }
      return newMode
    })
  }, [])

  // 現在のテーマを作成
  const theme = React.useMemo(() => createAppTheme(mode), [mode])

  // テーマコンテキストの値
  const themeContextValue = React.useMemo(
    () => ({
      toggleColorMode,
      mode,
    }),
    [toggleColorMode, mode],
  )

  return (
    <React.Suspense fallback={<LoadingContainer />}>
      <ErrorBoundary FallbackComponent={MainErrorFallback}>
        <HelmetProvider>
          <ThemeContext.Provider value={themeContextValue}>
            <MuiThemeProvider theme={theme}>
              <CssBaseline />
              <QueryClientProvider client={queryClient}>
                {import.meta.env.DEV && <ReactQueryDevtools />}
                <Notifications />
                <AuthLoader renderLoading={() => <LoadingContainer />}>{children}</AuthLoader>
              </QueryClientProvider>
            </MuiThemeProvider>
          </ThemeContext.Provider>
        </HelmetProvider>
      </ErrorBoundary>
    </React.Suspense>
  )
}
